<script setup>
import * as v from 'valibot'
import { vMaska } from "maska/vue"

const { $bus } = useNuxtApp();
const route = useRoute()
const { fetch: refreshSession } = useUserSession()

const props = defineProps({
    copy: {
        type: String,
        default: 'You must log in using the email you registered with and your ticket confirmation number.'
    },
    headline: {
        type: String,
        default: 'Login'
    },
})

const open = defineModel('open')

const auth_form = useTemplateRef('auth_form')
const busy = ref(false)

const schema = v.object({
    email: v.pipe(v.string(), v.trim(), v.email('Please enter a valid email address.')),
    ticketId: v.pipe(v.trim())
})

const state = reactive({
    email: '',
    ticketId: ''
})

const labelStyles = {
    label: 'text-teal-light font-light leading-[1.310125rem] tracking-[0.008125rem] text-[0.8125rem] w-full',
    container: 'w-full',
    root: 'w-full',
    error: 'mt-[0.375rem] text-error',
    help: 'text-[0.75rem]'
}

const inputStyles = {
    base: 'border-teal-light py-[1.25rem] px-[1.5rem] bg-transparent w-full',
    root: 'w-full'
}

const buttonStyles = {
    base: 'flex w-min bg-orange-dark mt-[0.875rem] hover:bg-teal hover:text-white'
}

const onSubmit = async (e) => {
    busy.value = true

    try {
        const { response } = await $fetch('/api/v1/user/registration', { method: 'POST', body: { email: state.email, ticketId: state.ticketId } } )
        await refreshSession()
        const { modal, ...newQuery } = route.query
        // const newFullPath = route.path + (Object.keys(newQuery).length ? '?' + new URLSearchParams(newQuery).toString() : '')
        if (route.query.action === 'braindate') await navigateTo({ path: '/braindate' })
        else await navigateTo({ path: route.path, query: newQuery })
        open.value = false
        $bus.emit('auth:login', response)
    } catch (error) {
        auth_form.value.setErrors([
            { name: 'email', message: 'Incorrect email or ticket number'  }
        ])
    } finally {
        busy.value = false
    }
}

</script>

<template>
    <div class='modal login-form'>
        <div class='inner'>
            <div class='header'>
                <h1 class='h1' v-html="headline"></h1>
                <p class='copy'>
                    {{ copy }}
                    If you haven't registered already, you can <nuxt-link to="/register">purchase your registration here</nuxt-link>.
                </p>
            </div>
            <div class='form'>
                <UForm :schema="schema" :state="state" @submit="onSubmit" ref="auth_form">
                    <UFormField label="Email" name="email" :ui="labelStyles" :class="{ 'blur-xs': busy }">
                        <UInput v-model="state.email" :ui="inputStyles" placeholder="name@example.com" />
                    </UFormField>
                    <UFormField label="Ticket Number" name="ticketId" :ui="labelStyles" help="Your ticket number can be found in your confirmation email." :class="{ 'blur-xs': busy }">
                        <UInput v-model="state.ticketId" type="text" :ui="inputStyles" v-maska="'########'" placeholder="12345678" />
                    </UFormField>
                    <UButton type="submit" :ui="buttonStyles" variant="login">
                        Login
                    </UButton>
                </UForm>
            </div>
            <div class='actions'>
                <p>Having problems logging in? Visit our <nuxt-link to="/help#login">help page</nuxt-link>.</p>
                <UButton label="Close" color="primary" variant="link" class="rounded-full" @click="open = false" />
            </div>
        </div>
    </div>
</template>

<style lang="scss">
    .modal.login-form {
        display: flex;
        flex-direction: column;
        align-items: center;
        width: 100%;
        max-width: 38.875rem;
        
        .inner {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 1.6875rem;

            .form {
                width: 100%;
                form { width: 100%; }
            }

            .h1 {
                text-align: center;
                font-size: 2rem;
                font-style: normal;
                font-weight: 500;
                line-height: 2.5rem;
                letter-spacing: -0.04rem;
            }

            .copy {
                text-align: center;
                font-size: 0.9375rem;
                font-style: normal;
                font-weight: 300;
                line-height: 1.5625rem;
                letter-spacing: 0.01875rem;

                a {
                    color: var(--teal);
                    text-decoration: underline;
                    text-underline-offset: 0.25rem;
                    @include hover 
                    {
                        color: var(--orange-dark);
                        text-decoration: none;
                    }
                }
            }

            .actions {
                display: flex;
                flex-direction: column;
                align-items: center;
                margin-bottom: 2.1875rem;

                p { 
                    color: var(--teal-light);
                    text-align: center;
                    font-size: 0.8125rem;
                    font-style: normal;
                    font-weight: 400;
                    line-height: 1.5625rem;
                    letter-spacing: 0.01625rem;

                    a {
                        text-decoration: underline;
                        text-underline-offset: 0.25rem;
                        @include hover {
                            color: var(--orange-dark);
                        }
                    }
                }

                button {
                    margin-top: 1.25rem;
                }
            }
        }
    }
</style>